import { ApiHosts, handleResponseOld, makeGet, makePost } from './helpers';

/**
 * Alert type IDs that match backend enum values
 */
export enum AlertTypes {
    All = 0, // All alerts is not a real alert type, but used to toggle all alerts
    Outbid = 1,
    AuctionReminder,
    RegistrationUpdates,
    BidsEndingSoon,
    FollowedAuctions,
    RateYourExperience,
    PaymentReminder,
    VisitedItems,
    RegistrationReceived,
    RegistrationApproved,
    RegistrationDeclined,
    SavedItemsEndingSoon,
    SearchAlerts,
}

type AlertTypeName =
    | 'Outbid Notifications'
    | 'Auction Reminders'
    | 'Registration Updates'
    | 'Bids Ending Soon'
    | 'New Auctions From Followed Auctioneers'
    | 'Rate Your Experience'
    | 'Payment Reminder'
    | 'Visited Items'
    | 'Registration Received'
    | 'Registration Approved'
    | 'Registration Declined'
    | 'Saved Items Ending Soon'
    | 'Search Alerts';

export enum DeliveryType {
    browser = 'b',
    email = 'e',
    sms = 's',
    unsubscribe = '',
}

/**
 * @category Notifications API
 * @see fetchSmsAlerts
 */
type FetchSmsAlertsParams = {
    authToken: string;
    deployment: string;
};

export type FetchSmsAlertsResponse = {
    error: boolean;
    payload: {
        alertTypeId: number;
        bidderId: number;
        deliveryType: DeliveryType;
        name: AlertTypeName;
        subscribed: boolean;
    }[];
};

/**
 * makes GET request for SMS alert settings via Telegram service
 * @function fetchSmsAlerts
 * @category Notifications API
 * @param {FetchSmsAlertsParams}
 * @see https://telegram-DEPLOYMENT.liveauctioneers.com/alertPreferences
 */
export const fetchSmsAlerts = ({ authToken, deployment }: FetchSmsAlertsParams) =>
    new Promise<FetchSmsAlertsResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'alertPreferences',
            authToken,
            deployment,
            path: ApiHosts.Notifications,
        });
        request.end((err: string, response: object) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Notifications API
 * @see toggleSmsAlert
 */
type ToggleSmsAlertsParams = {
    alertPreferences: {
        alertTypeId: AlertTypes;
        deliveryType: DeliveryType;
        subscribed: boolean;
    }[];
} & FetchSmsAlertsParams;

type ToggleSmsAlertsResponse = {
    error: boolean;
    payload: {
        alertTypeId: AlertTypes;
        bidderId: number;
        deliveryType: DeliveryType;
        name: AlertTypeName;
        subscribed: boolean;
    }[];
};

/**
 * makes POST request to toggle SMS alert settings via Telegram service
 * @function toggleSmsAlerts
 * @category Notifications API
 * @param {ToggleSmsAlertsParams}
 * @see https://telegram-DEPLOYMENT.liveauctioneers.com/alertPreferences
 */
export const toggleSmsAlerts = ({ alertPreferences, authToken, deployment }: ToggleSmsAlertsParams) =>
    new Promise<ToggleSmsAlertsResponse>((resolve, reject) => {
        const request = makePost({
            apiPath: 'alertPreferences',
            authToken,
            deployment,
            path: ApiHosts.Notifications,
        });

        const queries = alertPreferences.map(({ alertTypeId, deliveryType, subscribed }) => {
            const query = { alertTypeId, subscribed };
            if (deliveryType) {
                // @ts-expect-error - standardized deliveryType since backend is not updated.
                query.deliveryType = deliveryType;
            }
            return query;
        });

        request.send(queries);
        request.end((err: string, response: object) => handleResponseOld({ err, reject, resolve, response }));
    });
